import { useState } from 'react';
import { KpiCard, Card, Badge, toast, SlidePanel, Modal } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useRole } from '@/hooks/useRole';
import styles from './Billing.module.css';

const API = import.meta.env.VITE_API_URL || '/api';

const formatCurrency = (v: number) => `¥${v.toLocaleString('ja-JP')}`;

interface BillingItem {
  id: number; billing_id: number; description: string;
  unit_price: number; quantity: number; amount: number;
}

interface BillingRow {
  id: number; tenant_id: string; invoice_no: string;
  subtotal: number; tax: number; amount: number; allocated_amount: number;
  status: string; due_date: string | null; paid_at: string | null;
  payment_method: string | null; invoice_sent_at: string | null;
  period_start: string; period_end: string;
  company_name: string; plan_name: string; created_at: string;
}

interface PaymentRow {
  id: number; tenant_id: string; company_name: string;
  amount: number; allocated_total: number; received_at: string;
  reference_no: string | null; payment_method: string; notes: string | null;
}

interface BillingDetail extends BillingRow {
  items: BillingItem[];
}

interface BillingSummary {
  total_amount: number; paid_amount: number; unpaid_amount: number;
  total_count: number; paid_count: number; unpaid_count: number;
  prev_total_amount: number;
  new_mrr: number; new_tenant_count: number;
  churn_mrr: number; churn_count: number;
}

function formatYen(n: number) {
  return `¥${n.toLocaleString()}`;
}

function calcDelta(current: number, prev: number): { text: string; trend: 'up' | 'down' | 'flat' } {
  if (!prev) return { text: '—', trend: 'flat' };
  const pct = Math.round(((current - prev) / prev) * 100);
  if (pct > 0) return { text: `↑ +${pct}%`, trend: 'up' };
  if (pct < 0) return { text: `↓ ${pct}%`, trend: 'down' };
  return { text: '± 0%', trend: 'flat' };
}

const PAYMENT_LABEL: Record<string, string> = {
  credit_card: 'クレジットカード',
  bank_transfer: '銀行振込',
  invoice: '請求書払い',
};

export function Billing() {
  const { isAdmin } = useRole();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [detail, setDetail] = useState<BillingDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [invoiceHtml, setInvoiceHtml] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceBillingId, setInvoiceBillingId] = useState<number>(0);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'unpaid' | 'partial' | 'sent' | 'paid'>('all');

  // 入金登録モーダル
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [payForm, setPayForm] = useState({ tenant_id: '', amount: '', received_at: new Date().toISOString().slice(0,10), reference_no: '', payment_method: 'bank_transfer', notes: '' });

  // 引当モーダル
  const [allocModalOpen, setAllocModalOpen] = useState(false);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [allocTarget, setAllocTarget] = useState<{ payment: PaymentRow; billing: BillingRow } | null>(null);
  const [allocAmount, setAllocAmount] = useState('');

  const { data: invoices, loading, refetch: refetchInvoices } = useApi<BillingRow[]>(`/console/billing?month=${month}`);
  const { data: summary } = useApi<BillingSummary>(`/console/billing/summary?month=${month}`);

  const delta = summary ? calcDelta(summary.total_amount, summary.prev_total_amount) : { text: '—', trend: 'flat' as const };

  const kpis = summary ? [
    { label: '今月請求総額', value: formatYen(summary.total_amount), delta: delta.text, trend: delta.trend },
    { label: '未収入金', value: formatYen(summary.unpaid_amount), delta: summary.unpaid_count > 0 ? `${summary.unpaid_count}テナント未払い` : '—', trend: summary.unpaid_count > 0 ? 'down' as const : 'flat' as const },
    { label: '今月新規 MRR', value: formatYen(summary.new_mrr), delta: summary.new_tenant_count > 0 ? `↑ ${summary.new_tenant_count}テナント` : '—', trend: summary.new_tenant_count > 0 ? 'up' as const : 'flat' as const },
    { label: '解約 MRR', value: formatYen(summary.churn_mrr), delta: summary.churn_count > 0 ? `↓ ${summary.churn_count}テナント` : '—', trend: summary.churn_count > 0 ? 'down' as const : 'flat' as const },
  ] : [];

  const filteredInvoices = (invoices ?? []).filter((inv) => {
    const matchSearch = !searchText || inv.company_name.includes(searchText) || inv.invoice_no.includes(searchText);
    const matchStatus =
      statusFilter === 'all' ? true :
      statusFilter === 'paid' ? inv.status === 'paid' :
      statusFilter === 'partial' ? inv.status === 'partial' :
      statusFilter === 'sent' ? (inv.status === 'unpaid' && !!inv.invoice_sent_at) :
      statusFilter === 'unpaid' ? (inv.status === 'unpaid' && !inv.invoice_sent_at) :
      true;
    return matchSearch && matchStatus;
  });

  // 入金登録
  const handleRegisterPayment = async () => {
    if (!payForm.tenant_id || !payForm.amount || !payForm.received_at) {
      toast('テナント・金額・入金日は必須です', 'error'); return;
    }
    try {
      await api('/console/payments', { method: 'POST', body: JSON.stringify({ ...payForm, amount: Number(payForm.amount) }) });
      toast('入金を登録しました');
      setPaymentModalOpen(false);
      setPayForm({ tenant_id: '', amount: '', received_at: new Date().toISOString().slice(0,10), reference_no: '', payment_method: 'bank_transfer', notes: '' });
      refetchInvoices();
    } catch (e) { toast(e instanceof Error ? e.message : '登録に失敗しました', 'error'); }
  };

  // 引当一覧を開く
  const openAllocModal = async () => {
    try {
      const data = await api<PaymentRow[]>(`/console/payments`);
      setPayments(data);
      setAllocModalOpen(true);
    } catch { toast('入金一覧の取得に失敗しました', 'error'); }
  };

  // 引当実行
  const handleAllocate = async () => {
    if (!allocTarget || !allocAmount) { toast('金額を入力してください', 'error'); return; }
    const amt = Number(allocAmount);
    const remaining = allocTarget.payment.amount - allocTarget.payment.allocated_total;
    if (amt <= 0 || amt > remaining) { toast(`引当可能額: ¥${remaining.toLocaleString()}`, 'error'); return; }
    try {
      await api(`/console/payments/${allocTarget.payment.id}/allocate`, {
        method: 'POST',
        body: JSON.stringify({ allocations: [{ billing_id: allocTarget.billing.id, amount: amt }] }),
      });
      toast(`¥${amt.toLocaleString()} を ${allocTarget.billing.invoice_no} に引当しました`);
      setAllocTarget(null); setAllocAmount('');
      const data = await api<PaymentRow[]>(`/console/payments?month=${month}`);
      setPayments(data);
      refetchInvoices();
    } catch (e) { toast(e instanceof Error ? e.message : '引当に失敗しました', 'error'); }
  };

  // 自動消込
  const handleAutoReconcile = async () => {
    try {
      const r = await api<{ matched: number }>('/console/billing/auto-reconcile', { method: 'POST', body: JSON.stringify({ month }) });
      toast(`自動消込完了: ${r.matched}件を消込しました`);
      refetchInvoices();
    } catch (e) { toast(e instanceof Error ? e.message : '自動消込に失敗しました', 'error'); }
  };

  const openDetail = async (id: number) => {
    try {
      const d = await api<BillingDetail>(`/console/billing/${id}`);
      setDetail(d);
      setDetailOpen(true);
    } catch { toast('詳細の取得に失敗しました'); }
  };

  const handleInvoicePdf = async (inv: BillingRow, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/console/billing/${inv.id}/invoice`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('mirai_token')}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: string }));
        throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
      }
      const html = await res.text();
      setInvoiceHtml(html);
      setInvoiceNo(inv.invoice_no);
      setInvoiceBillingId(inv.id);
      setInvoiceModalOpen(true);
    } catch (err) { toast(err instanceof Error ? err.message : '請求書の取得に失敗しました', 'error'); }
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/console/billing/${invoiceBillingId}/invoice/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('mirai_token')}` },
      });
      if (!res.ok) throw new Error('PDF生成に失敗しました');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `請求書 ${invoiceNo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast(e instanceof Error ? e.message : 'PDF生成に失敗しました', 'error'); }
    finally { setPdfLoading(false); }
  };

  // 月選択用の選択肢を生成（直近6ヶ月）
  const monthOptions: { value: string; label: string }[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${d.getFullYear()}年${d.getMonth() + 1}月`;
    monthOptions.push({ value: val, label });
  }

  return (
    <div className={styles.page}>
      <div className={styles.kpiRow}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <Card title="💳 請求一覧" actions={<>
        <select className={styles.monthSelect} value={month} onChange={(e) => setMonth(e.target.value)}>
          {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {isAdmin && <button className={styles.btnPrimary} onClick={async () => {
          try {
            const res = await api<{ generated: number; skipped: number; month: string }>('/console/billing/generate', { method: 'POST', body: JSON.stringify({ month }) });
            if (res.generated > 0) {
              toast(`${res.generated}件の請求を生成しました${res.skipped > 0 ? `（${res.skipped}件は既存のためスキップ）` : ''}`);
              window.location.reload();
            } else {
              toast(`生成対象がありません（${res.skipped}件は既存）`, 'info');
            }
          } catch { toast('請求生成に失敗しました', 'error'); }
        }}>＋ 請求生成</button>}
        {isAdmin && <button className={styles.btnSecondary} onClick={async () => {
          const targets = (invoices ?? []).filter((i) => i.status === 'unpaid' && !i.invoice_sent_at);
          if (!targets.length) {
            const alreadySent = (invoices ?? []).filter((i) => i.status === 'unpaid' && i.invoice_sent_at).length;
            toast(alreadySent > 0 ? `未送付の請求はありません（${alreadySent}件は送付済み）` : '送付対象の請求がありません', 'info');
            return;
          }
          let sent = 0; let failed = 0;
          for (const inv of targets) {
            try { await api(`/console/billing/${inv.id}/send-invoice`, { method: 'POST' }); sent++; } catch { failed++; }
          }
          if (sent > 0 && failed === 0) {
            toast(`${sent}件の請求書を送付しました`);
            window.location.reload();
          } else if (sent > 0) {
            toast(`${sent}件送付、${failed}件失敗（ユーザー未登録テナント）`, 'info');
            window.location.reload();
          } else {
            toast(`送付できませんでした（${failed}件のテナントにユーザーが未登録）`, 'error');
          }
        }}>📧 一括送付</button>}
        {isAdmin && <button className={styles.btnSecondary} onClick={() => setPaymentModalOpen(true)}>💰 入金登録</button>}
        {isAdmin && <button className={styles.btnSecondary} onClick={openAllocModal}>🔗 引当</button>}
        {isAdmin && <button className={styles.btnSecondary} onClick={handleAutoReconcile}>✅ 自動消込</button>}
        <button className={styles.btnSecondary} onClick={async () => {
          try {
            const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/console/billing/export?month=${month}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('mirai_token')}` },
            });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `billing_${month}.csv`; a.click();
            URL.revokeObjectURL(url);
            toast('CSVをダウンロードしました');
          } catch { toast('エクスポートに失敗しました', 'error'); }
        }}>📤 エクスポート</button>
      </>}>
        <div className={styles.filterBar}>
          <div className={styles.filterSearch}>
            <span className={styles.filterSearchIcon}>🔍</span>
            <input
              type="text"
              className={styles.filterSearchInput}
              placeholder="テナント名・請求番号で検索…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <option value="all">全て</option>
            <option value="unpaid">未払い</option>
            <option value="partial">一部入金</option>
            <option value="sent">送付済</option>
            <option value="paid">支払済</option>
          </select>
        </div>
        {loading && <div className={styles.empty}>読み込み中...</div>}
        {!loading && filteredInvoices.length === 0 && (
          <div className={styles.empty}>
            {(invoices ?? []).length === 0 ? '該当する請求データがありません' : '条件に一致する請求がありません'}
          </div>
        )}
        {filteredInvoices.map((inv) => (
          <div key={inv.id} className={styles.billingRow} onClick={() => openDetail(inv.id)}>
            <div style={{ flex: 1 }}>
              <div className={styles.tdMain}>{inv.company_name}</div>
              <div className={styles.tdSub}>{inv.invoice_no} · {inv.plan_name}</div>
            </div>
            <div className={styles.colRight}>
              <div className={styles.amount}>¥{inv.amount.toLocaleString()}</div>
              <div className={styles.taxLabel}>（税 ¥{inv.tax.toLocaleString()}）</div>
            </div>
            <div className={styles.period}>
              {inv.period_start ? new Date(inv.period_start).toLocaleDateString('ja-JP') : ''} → {inv.period_end ? new Date(inv.period_end).toLocaleDateString('ja-JP') : ''}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <Badge variant={inv.status === 'paid' ? 'green' : inv.status === 'partial' ? 'blue' : 'amber'}>
                {inv.status === 'paid' ? '支払済' : inv.status === 'partial' ? '一部入金' : inv.invoice_sent_at ? '送付済' : '未払い'}
              </Badge>
              {inv.status === 'partial' && (
                <span style={{ fontSize: 10, color: 'var(--ink3)' }}>
                  ¥{(inv.allocated_amount ?? 0).toLocaleString()} / ¥{inv.amount.toLocaleString()}
                </span>
              )}
            </div>
            <button className={styles.btnSm} onClick={(e) => handleInvoicePdf(inv, e)}>📄 請求書</button>
            {inv.status === 'paid' ? (
              <button className={styles.btnSm} onClick={async (e) => {
                e.stopPropagation();
                try {
                  const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/console/billing/${inv.id}/receipt`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('mirai_token')}` },
                  });
                  if (!res.ok) {
                    const body = await res.json().catch(() => ({} as { error?: string }));
                    throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
                  }
                  const html = await res.text();
                  const w = window.open('', '_blank');
                  if (w) { w.document.write(html); w.document.close(); }
                } catch (err) { toast(err instanceof Error && err.message ? err.message : '領収書の取得に失敗しました', 'error'); }
              }}>領収書</button>
            ) : (<>
              {isAdmin && <button className={styles.btnWarn} onClick={async (e) => {
                e.stopPropagation();
                try {
                  await api(`/console/billing/${inv.id}/remind`, { method: 'POST' });
                  toast('督促メールを送信しました');
                } catch (err) { toast(err instanceof Error ? err.message : '督促メール送信に失敗しました', 'error'); }
              }}>督促</button>}
            </>)}
          </div>
        ))}
      </Card>

      {/* 入金登録モーダル */}
      <Modal open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} title="💰 入金登録"
        footer={<button className={styles.btnPrimary} onClick={handleRegisterPayment}>登録する</button>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: 13 }}>テナント（請求一覧から選択）
            <select className={styles.monthSelect} style={{ width: '100%', marginTop: 4 }}
              value={payForm.tenant_id} onChange={e => setPayForm(f => ({ ...f, tenant_id: e.target.value }))}>
              <option value="">-- 選択 --</option>
              {[...new Map((invoices ?? []).map(i => [i.tenant_id, i])).values()].map(i => (
                <option key={i.tenant_id} value={i.tenant_id}>{i.company_name}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>入金金額（税込）
            <input type="number" className={styles.monthSelect} style={{ width: '100%', marginTop: 4 }}
              value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} placeholder="例: 27500" />
          </label>
          <label style={{ fontSize: 13 }}>入金日
            <input type="date" className={styles.monthSelect} style={{ width: '100%', marginTop: 4 }}
              value={payForm.received_at} onChange={e => setPayForm(f => ({ ...f, received_at: e.target.value }))} />
          </label>
          <label style={{ fontSize: 13 }}>振込番号・参考番号（任意）
            <input type="text" className={styles.monthSelect} style={{ width: '100%', marginTop: 4 }}
              value={payForm.reference_no} onChange={e => setPayForm(f => ({ ...f, reference_no: e.target.value }))} placeholder="例: 振込0001" />
          </label>
          <label style={{ fontSize: 13 }}>支払方法
            <select className={styles.monthSelect} style={{ width: '100%', marginTop: 4 }}
              value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}>
              <option value="bank_transfer">銀行振込</option>
              <option value="credit_card">クレジットカード</option>
              <option value="invoice">請求書払い</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>メモ（任意）
            <input type="text" className={styles.monthSelect} style={{ width: '100%', marginTop: 4 }}
              value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
          </label>
        </div>
      </Modal>

      {/* 引当モーダル */}
      <Modal open={allocModalOpen} onClose={() => { setAllocModalOpen(false); setAllocTarget(null); }} title="🔗 入金引当" width={800}>
        {!allocTarget ? (
          <div>
            <p style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 12 }}>引当する入金を選択してください。</p>
            {payments.length === 0 && <div style={{ color: 'var(--ink3)', fontSize: 13 }}>入金記録がありません。先に「💰 入金登録」を行ってください。</div>}
            {payments.map(pay => {
              const remaining = pay.amount - pay.allocated_total;
              return (
                <div key={pay.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{pay.company_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{pay.received_at} ｜ {PAYMENT_LABEL[pay.payment_method] ?? pay.payment_method}{pay.reference_no ? ` ｜ ${pay.reference_no}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13 }}>
                    <div>¥{pay.amount.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: remaining > 0 ? 'var(--amber)' : 'var(--green)' }}>残: ¥{remaining.toLocaleString()}</div>
                  </div>
                  {remaining > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(invoices ?? []).filter(i => i.tenant_id === pay.tenant_id && i.status !== 'paid').map(inv => (
                        <button key={inv.id} className={styles.btnSm}
                          onClick={() => { setAllocTarget({ payment: pay, billing: inv }); setAllocAmount(String(Math.min(remaining, inv.amount - (inv.allocated_amount ?? 0)))); }}>
                          {inv.invoice_no} ¥{(inv.amount - (inv.allocated_amount ?? 0)).toLocaleString()}未充当
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: 8 }}>
            <p style={{ fontSize: 13, marginBottom: 12 }}>
              <strong>{allocTarget.payment.company_name}</strong> の入金（¥{allocTarget.payment.amount.toLocaleString()}）を
              <strong> {allocTarget.billing.invoice_no}</strong> に引当します。
            </p>
            <label style={{ fontSize: 13 }}>引当金額（最大: ¥{Math.min(allocTarget.payment.amount - allocTarget.payment.allocated_total, allocTarget.billing.amount - (allocTarget.billing.allocated_amount ?? 0)).toLocaleString()}）
              <input type="number" className={styles.monthSelect} style={{ width: '100%', marginTop: 4 }}
                value={allocAmount} onChange={e => setAllocAmount(e.target.value)} />
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className={styles.btnPrimary} onClick={handleAllocate}>引当実行</button>
              <button className={styles.btnSecondary} onClick={() => { setAllocTarget(null); setAllocAmount(''); }}>戻る</button>
            </div>
          </div>
        )}
      </Modal>

      {/* 請求書モーダル */}
      <Modal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        title="請求書"
        width={760}
        bodyStyle={{ padding: 0, overflow: 'hidden' }}
        footer={
          <button className={styles.btnPrimary} onClick={handleDownloadPdf} disabled={pdfLoading}>{pdfLoading ? 'PDF生成中...' : '📥 PDFダウンロード'}</button>
        }
      >
        <iframe
          srcDoc={invoiceHtml}
          style={{ width: '100%', height: 'calc(95vh - 116px)', border: 'none', background: '#fff', display: 'block' }}
          title="請求書プレビュー"
        />
      </Modal>

      {/* 請求詳細スライドパネル */}
      <SlidePanel open={detailOpen} onClose={() => setDetailOpen(false)} title="請求詳細">
        {detail && (
          <div className={styles.detailPanel}>
            <div className={styles.detailHeader}>
              <h3>{detail.company_name}</h3>
              <Badge variant={detail.status === 'paid' ? 'green' : 'amber'}>
                {detail.status === 'paid' ? '支払済' : '未払い'}
              </Badge>
            </div>

            <div className={styles.detailMeta}>
              <div><span className={styles.metaLabel}>請求番号</span>{detail.invoice_no}</div>
              <div><span className={styles.metaLabel}>プラン</span>{detail.plan_name}</div>
              <div><span className={styles.metaLabel}>対象期間</span>{new Date(detail.period_start).toLocaleDateString('ja-JP')} → {new Date(detail.period_end).toLocaleDateString('ja-JP')}</div>
              <div><span className={styles.metaLabel}>支払期限</span>{detail.due_date ? new Date(detail.due_date).toLocaleDateString('ja-JP') : '—'}</div>
              {detail.paid_at && <div><span className={styles.metaLabel}>支払日</span>{new Date(detail.paid_at).toLocaleDateString('ja-JP')}</div>}
              {detail.payment_method && <div><span className={styles.metaLabel}>支払方法</span>{PAYMENT_LABEL[detail.payment_method] ?? detail.payment_method}</div>}
            </div>

            {detail.items.length > 0 && (
              <div className={styles.itemsSection}>
                <h4>明細</h4>
                <table className={styles.itemsTable}>
                  <thead>
                    <tr><th>内容</th><th>単価</th><th>数量</th><th>金額</th></tr>
                  </thead>
                  <tbody>
                    {detail.items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td className={styles.numCell}>¥{item.unit_price.toLocaleString()}</td>
                        <td className={styles.numCell}>{item.quantity}</td>
                        <td className={styles.numCell}>¥{item.amount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className={styles.totalSection}>
              <div className={styles.totalRow}><span>小計</span><span>¥{detail.subtotal.toLocaleString()}</span></div>
              <div className={styles.totalRow}><span>消費税（10%）</span><span>¥{detail.tax.toLocaleString()}</span></div>
              <div className={`${styles.totalRow} ${styles.grandTotal}`}><span>合計</span><span>¥{detail.amount.toLocaleString()}</span></div>
            </div>
          </div>
        )}
      </SlidePanel>
    </div>
  );
}
